import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Instagram, Linkedin, Mail, MessageCircleMore, FileText, Youtube, Sparkles, Calendar } from "lucide-react";
import { useMemo } from "react";

const CHANNEL_ICON = {
    "Instagram Post": Instagram, "Instagram Reel": Instagram, "Instagram Stories": Instagram,
    "LinkedIn": Linkedin, "Newsletter": Mail, "WhatsApp": MessageCircleMore,
    "WhatsApp Broadcast": MessageCircleMore, "Blog": FileText, "Video YouTube": Youtube,
};
const CHANNEL_COLOR = {
    "Instagram Post": "text-pink-500", "Instagram Reel": "text-pink-500", "Instagram Stories": "text-pink-500",
    "LinkedIn": "text-sky-600", "Newsletter": "text-primary", "WhatsApp": "text-emerald-600",
    "Blog": "text-textmuted", "Video YouTube": "text-red-500",
};

export default function MarketingPage() {
    const q = useQuery({ queryKey: ["content"], queryFn: () => api.get("/marketing/content").then((r) => r.data) });
    const wq = useQuery({ queryKey: ["webinars"], queryFn: () => api.get("/webinars").then((r) => r.data) });

    const items = q.data || [];
    const grouped = useMemo(() => {
        const g = {};
        items.forEach((it) => {
            const day = it.date?.slice(0, 10);
            g[day] = g[day] || [];
            g[day].push(it);
        });
        return g;
    }, [items]);

    return (
        <div className="space-y-8 pv-enter">
            <div>
                <div className="pv-eyebrow mb-2">Marketing</div>
                <h1 className="pv-serif text-4xl tracking-tight">Calendario editoriale</h1>
                <p className="text-[13.5px] text-textmuted mt-2 max-w-2xl">
                    Idee generate ogni giorno dall'AI, distribuite tra canali. Ogni contenuto è collegato a un obiettivo,
                    a un pubblico e a un tono psicologico (5 motivazioni).
                </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-3">
                    {Object.entries(grouped).map(([day, arr]) => (
                        <div key={day} className="pv-card p-5">
                            <div className="flex items-center gap-2 mb-3">
                                <Calendar size={13} strokeWidth={1.5} className="text-textmuted" />
                                <div className="text-[12.5px] font-medium">{new Date(day).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}</div>
                            </div>
                            <div className="space-y-2">
                                {arr.map((c) => <ContentRow key={c.id} c={c} />)}
                            </div>
                        </div>
                    ))}
                    {items.length === 0 && <div className="pv-card p-8 text-center text-textsubtle text-[13px]">Nessun contenuto in calendario.</div>}
                </div>

                <div className="space-y-4">
                    <div className="pv-card p-5">
                        <div className="pv-eyebrow mb-1 flex items-center gap-2"><Sparkles size={11} strokeWidth={1.5} />AI Marketing Strategist</div>
                        <div className="text-[13.5px] leading-relaxed">
                            Il Reel del 12/02 ha generato il 38% dei lead IG del mese.
                            Replica il formato con 3 nuovi soggetti clinici e un gancio "perdita di tempo nello studio".
                        </div>
                    </div>

                    <div className="pv-card p-5">
                        <div className="pv-eyebrow mb-3">Webinar</div>
                        <div className="space-y-2">
                            {(wq.data || []).map((w) => (
                                <div key={w.id} className="pv-hover p-2.5 rounded-lg border border-border/[var(--border-opacity)]">
                                    <div className="text-[13px] font-medium leading-snug">{w.title}</div>
                                    <div className="flex items-center gap-3 text-[11px] text-textmuted mt-1.5">
                                        <span>{w.date}</span>
                                        <span>·</span>
                                        <span>{w.attended}/{w.registered} presenti</span>
                                        <span>·</span>
                                        <span>{w.demos_generated} demo</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function ContentRow({ c }) {
    const Icon = CHANNEL_ICON[c.channel] || FileText;
    return (
        <div className="flex items-start gap-3 pv-hover px-3 py-2.5 rounded-lg border border-border/[var(--border-opacity)]">
            <div className={`h-8 w-8 rounded-lg bg-surface2 flex items-center justify-center flex-shrink-0 ${CHANNEL_COLOR[c.channel] || "text-textmuted"}`}>
                <Icon size={14} strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[11px] text-textsubtle uppercase tracking-wider">{c.channel}</span>
                    <span className="text-[11px] px-1.5 py-0 rounded bg-primary/[0.08] text-primary">{c.motivation}</span>
                    <span className="text-[11px] px-1.5 py-0 rounded bg-textsubtle/10 text-textmuted">{c.status}</span>
                </div>
                <div className="text-[13.5px] mt-0.5 leading-snug">{c.title}</div>
                <div className="text-[11px] text-textsubtle mt-0.5">CTA: {c.cta} · Pubblico: {c.audience} · {c.assignee}</div>
            </div>
        </div>
    );
}
